import React, { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Chip,
} from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { gruposApi } from '../../api/grupos'
import { empresasApi } from '../../api/empresas'
import { UserManutencao, UserManutencaoCreate, UserManutencaoUpdate } from '../../api/usuariosManutencao'
import { useAuth } from '../../contexts/AuthContext'
import { NivelAcesso } from '../../types/auth'

// Schema de validação para criação
const createSchema = z.object({
  email: z.string().email('Email inválido'),
  name: z.string().min(1, 'Nome é obrigatório').max(100, 'Nome deve ter no máximo 100 caracteres'),
  password: z.string().min(6, 'Senha deve ter no mínimo 6 caracteres'),
  nivel_acesso: z.number().min(0).max(5).optional(),
  is_active: z.boolean(),
  descricao_usuario: z.string().optional().nullable(),
  grupo_ids: z.array(z.number()).min(1, 'Selecione pelo menos um grupo'),
  empresa_ids: z.array(z.number()).optional(),
  login_thor: z.string().max(16, 'Login ThorJava deve ter no máximo 16 caracteres').optional().nullable(),
  iduser_thor: z.number().int().nonnegative().optional().nullable(),
})

// Schema de validação para edição
const updateSchema = z.object({
  name: z.string().min(1, 'Nome é obrigatório').max(100, 'Nome deve ter no máximo 100 caracteres'),
  nivel_acesso: z.number().min(0).max(5).optional().nullable(),
  is_active: z.boolean().optional().nullable(),
  descricao_usuario: z.string().optional().nullable(),
  login_thor: z.string().max(16, 'Login ThorJava deve ter no máximo 16 caracteres').optional().nullable(),
  iduser_thor: z.number().int().nonnegative().optional().nullable(),
})

type CreateFormData = z.infer<typeof createSchema>
type UpdateFormData = z.infer<typeof updateSchema>

interface UsuarioManutencaoFormProps {
  open: boolean
  onClose: () => void
  onSubmit: (data: UserManutencaoCreate | UserManutencaoUpdate) => void
  usuario?: UserManutencao | null
  loading?: boolean
  mode: 'create' | 'edit'
}

const UsuarioManutencaoForm: React.FC<UsuarioManutencaoFormProps> = ({
  open,
  onClose,
  onSubmit,
  usuario,
  loading = false,
  mode,
}) => {
  const { user: currentUser } = useAuth()
  const isSuperAdmin = currentUser?.nivel_acesso === NivelAcesso.SUPER_ADMIN

  // Buscar grupos (apenas para criação ou super admin)
  const { data: grupos = [] } = useQuery({
    queryKey: ['grupos', 'form'],
    queryFn: () => gruposApi.list({ includeInactive: true }),
    enabled: open && (mode === 'create' || isSuperAdmin),
  })

  // Buscar empresas (apenas para criação)
  const { data: empresas = [] } = useQuery({
    queryKey: ['empresas', 'form'],
    queryFn: () => empresasApi.list(),
    enabled: open && mode === 'create',
  })

  const formSchema = mode === 'create' ? createSchema : updateSchema
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm<CreateFormData | UpdateFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: mode === 'create' ? {
      email: '',
      name: '',
      password: '',
      nivel_acesso: 5,
      is_active: true,
      descricao_usuario: '',
      grupo_ids: [],
      empresa_ids: [],
      login_thor: '',
      iduser_thor: null,
    } : {
      name: usuario?.name || '',
      nivel_acesso: usuario?.nivel_acesso ?? null,
      is_active: usuario?.is_active ?? true,
      descricao_usuario: usuario?.descricao_usuario || '',
      login_thor: usuario?.login_thor || '',
      iduser_thor: usuario?.iduser_thor ?? null,
    },
  })

  const grupoIds = watch('grupo_ids' as any) || []
  const empresaIds = watch('empresa_ids' as any) || []
  const isActive = watch('is_active' as any) ?? true
  const nivelAcesso = watch('nivel_acesso' as any)

  useEffect(() => {
    if (open) {
      if (mode === 'create') {
        reset({
          email: '',
          name: '',
          password: '',
          nivel_acesso: 5,
          is_active: true,
          descricao_usuario: '',
          grupo_ids: [],
          empresa_ids: [],
          login_thor: '',
          iduser_thor: null,
        })
      } else {
        reset({
          name: usuario?.name || '',
          nivel_acesso: usuario?.nivel_acesso ?? null,
          is_active: usuario?.is_active ?? true,
          descricao_usuario: usuario?.descricao_usuario || '',
          login_thor: usuario?.login_thor || '',
          iduser_thor: usuario?.iduser_thor ?? null,
        })
      }
    }
  }, [open, usuario, mode, reset])

  const handleFormSubmit = async (data: any) => {
    // Converter strings vazias em null para descrições
    if (data.descricao_usuario === '') {
      data.descricao_usuario = null
    }
    // Converter iduser_thor vazio ou 0 em null
    if (data.iduser_thor === '' || data.iduser_thor === 0 || data.iduser_thor === null) {
      data.iduser_thor = null
    }
    onSubmit(data)
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  // removido: getNivelName (não utilizado)

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <DialogTitle>{mode === 'create' ? 'Criar Usuário' : 'Editar Usuário'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {mode === 'create' && (
              <>
                <TextField
                  {...register('email' as any)}
                  label="Email"
                  fullWidth
                  margin="normal"
                  type="email"
                  error={!!(errors as any)?.email}
                  helperText={(errors as any)?.email?.message}
                  disabled={loading}
                  autoFocus
                />

                <TextField
                  {...register('password' as any)}
                  label="Senha"
                  fullWidth
                  margin="normal"
                  type="password"
                  error={!!(errors as any)?.password}
                  helperText={(errors as any)?.password?.message || 'Mínimo de 6 caracteres'}
                  disabled={loading}
                />

                <FormControl fullWidth margin="normal">
                  <InputLabel>Grupos *</InputLabel>
                  <Select
                    multiple
                    value={grupoIds}
                    label="Grupos *"
                    onChange={(e) => setValue('grupo_ids' as any, e.target.value as number[])}
                    disabled={loading}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(selected as number[]).map((id) => {
                          const grupo = grupos.find((g) => g.id === id)
                          return <Chip key={id} label={grupo?.nome || id} size="small" />
                        })}
                      </Box>
                    )}
                  >
                    {grupos.map((grupo) => (
                      <MenuItem key={grupo.id} value={grupo.id}>
                        {grupo.nome}
                      </MenuItem>
                    ))}
                  </Select>
                  {(errors as any)?.grupo_ids && (
                    <Box sx={{ color: 'error.main', fontSize: '0.75rem', mt: 0.5, ml: 1.75 }}>
                      {(errors as any)?.grupo_ids?.message as string}
                    </Box>
                  )}
                </FormControl>

                <FormControl fullWidth margin="normal">
                  <InputLabel>Empresas (Opcional)</InputLabel>
                  <Select
                    multiple
                    value={empresaIds}
                    label="Empresas (Opcional)"
                    onChange={(e) => setValue('empresa_ids' as any, e.target.value as number[])}
                    disabled={loading}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(selected as number[]).map((id) => {
                          const empresa = empresas.find((e) => e.id === id)
                          return <Chip key={id} label={empresa?.nome || id} size="small" color="primary" />
                        })}
                      </Box>
                    )}
                  >
                    {empresas.map((empresa) => (
                      <MenuItem key={empresa.id} value={empresa.id}>
                        {empresa.nome}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </>
            )}

            <TextField
              {...register('name' as any)}
              label="Nome"
              fullWidth
              margin="normal"
              error={!!errors.name}
              helperText={errors.name?.message}
              disabled={loading}
              autoFocus={mode === 'edit'}
            />

            <TextField
              {...register('descricao_usuario' as any)}
              label="Descrição do Usuário"
              fullWidth
              margin="normal"
              multiline
              rows={3}
              error={!!errors.descricao_usuario}
              helperText={errors.descricao_usuario?.message}
              disabled={loading}
            />

            <FormControl fullWidth margin="normal">
              <InputLabel>Nível de Acesso</InputLabel>
              <Select
                value={nivelAcesso ?? ''}
                label="Nível de Acesso"
                onChange={(e) => setValue('nivel_acesso' as any, e.target.value === '' ? null : Number(e.target.value))}
                disabled={loading}
              >
                <MenuItem value={0}>Super Admin</MenuItem>
                <MenuItem value={1}>Admin</MenuItem>
                <MenuItem value={2}>Gerente</MenuItem>
                <MenuItem value={3}>Supervisor</MenuItem>
                <MenuItem value={4}>Operador</MenuItem>
                <MenuItem value={5}>Padrão</MenuItem>
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={isActive}
                  onChange={(e) => setValue('is_active' as any, e.target.checked)}
                  disabled={loading}
                />
              }
              label="Usuário Ativo"
              sx={{ mt: 2 }}
            />

            {/* Campo login_thor - visível para todos, mas editável apenas para Super Admin */}
            <TextField
              {...register('login_thor' as any)}
              label="Login ThorJava (até 16 caracteres, case sensitive)"
              fullWidth
              margin="normal"
              disabled={loading || !isSuperAdmin}
              inputProps={{ maxLength: 16 }}
              helperText={
                (errors as any)?.login_thor?.message || 
                (isSuperAdmin 
                  ? 'Corresponde ao campo login da vw_uccaduser. Pode ser usado para login alternativo ao email.' 
                  : 'Apenas Super Administradores podem editar este campo')
              }
              error={!!(errors as any)?.login_thor}
            />

            {/* Campo iduser_thor - visível para todos, mas editável apenas para Super Admin */}
            <TextField
              {...register('iduser_thor' as any, { 
                valueAsNumber: true,
                setValueAs: (v) => {
                  if (v === '' || v === null || v === undefined) return null
                  const num = Number(v)
                  return isNaN(num) ? null : num
                }
              })}
              label="ID Usuário ThorJava"
              fullWidth
              margin="normal"
              type="number"
              disabled={loading || !isSuperAdmin}
              helperText={
                (errors as any)?.iduser_thor?.message || 
                (isSuperAdmin 
                  ? 'ID único do usuário no ThorJava (uccaduser.iduser). Usado para identificação única e sincronização entre sistemas.' 
                  : 'Apenas Super Administradores podem editar este campo')
              }
              error={!!(errors as any)?.iduser_thor}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Cancelar
          </Button>
          <Button type="submit" variant="contained" disabled={loading}>
            {mode === 'create' ? 'Criar' : 'Salvar'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export default UsuarioManutencaoForm

